import {
  ClerkProvider,
  SignedIn,
  SignedOut,
  SignIn,
  UserButton,
  useAuth,
  useUser,
} from "@clerk/clerk-react";
import type { Agent, Channel, Thread, Workspace } from "@kritaka/adapter";
import type { DataContext } from "@kritaka/core";
import { getChannelMessages, listAgents, listChannels, listDrafts, sendMessage } from "@kritaka/core";
import {
  AddAgentDialog,
  AgentList,
  AgentSettingsDialog,
  AgentTerminal,
  AgentV2ChatView,
  type AgentV2Block,
  type AgentV2Message,
  type AgentV2Roster,
  type SystemPromptVariants,
  ChannelContextMenu,
  ChannelList,
  ChannelView,
  CollapsibleSection,
  ConnectRuntimePanel,
  configurePeerManager,
  createBrowserHubClient,
  createBrowserShellBase,
  CreateChannelDialog,
  EditChannelDialog,
  createRelayClient,
  createRelayPtyAdapter,
  DataContextProvider,
  DecisionsSidebar,
  DevicePage,
  RelayProvider,
  ScoutList,
  ScoutView,
  useDataContext,
  useRelay,
  KritakaWordmark,
  ThemeToggle,
  WorkspaceList,
  WORKSPACE_COLUMN_WIDTH,
  WorkspaceKnowledgePage,
  WorkspaceSettingsPage,
  type WorkspaceKnowledgeTab,
  type WorkspaceSettingsTab,
} from "@kritaka/ui";
import type { AgentRuntimeHost, HubClient } from "@kritaka/core";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useMatch,
  useNavigate,
  useParams,
} from "react-router-dom";
import { config } from "./config.js";
import { playMentionSound } from "./lib/sounds.js";
import { usePendingDecisionsByWorkspace } from "./lib/use-pending-decisions-by-workspace.js";

export function App() {
  // BrowserRouter wraps both auth states so the /device route can be public:
  // daemon-opened pairing links land on /device?code=… whether or not the
  // user has an active Clerk session, and we preserve that URL through the
  // sign-in flow via Clerk's signInFallbackRedirectUrl.
  return (
    <ClerkProvider publishableKey={config.clerkPublishableKey}>
      <BrowserRouter>
        <Routes>
          <Route path="/device" element={<DeviceRoute />} />
          <Route
            path="*"
            element={
              <>
                <SignedOut>
                  <SignInScreen />
                </SignedOut>
                <SignedIn>
                  <SignedInApp />
                </SignedIn>
              </>
            }
          />
        </Routes>
      </BrowserRouter>
    </ClerkProvider>
  );
}

function SignInScreen({ preserveCurrentUrl = false }: { preserveCurrentUrl?: boolean } = {}) {
  // For the device-pair flow we *must* land back on /device?code=… after
  // sign-in or the user pops out into the workspace shell and never sees
  // the approval card. `fallbackRedirectUrl` is "use this if nothing else
  // is set" — Clerk's own afterSignIn defaults can override it depending
  // on org/dashboard config, so we use `forceRedirectUrl` to make the
  // redirect mandatory in the device case. For the generic catchall sign-
  // in (no preserveCurrentUrl), we leave both unset so Clerk falls back
  // to its standard "/" landing.
  const preservedUrl = preserveCurrentUrl
    ? `${window.location.pathname}${window.location.search}`
    : undefined;
  return (
    <div
      className="app-pre-shell"
      style={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: "var(--bg-primary)",
      }}
    >
      <div style={{ textAlign: "center" }}>
        <h1 style={{ marginBottom: 24, color: "var(--text-primary)" }}>Kritaka</h1>
        <SignIn
          routing="hash"
          forceRedirectUrl={preservedUrl}
          signUpForceRedirectUrl={preservedUrl}
        />
      </div>
    </div>
  );
}

/**
 * `/device` route host. Delegates to DevicePage when signed in; on signed-out
 * renders the sign-in form with the current `/device?code=…` URL preserved
 * so Clerk bounces the user straight back to the approval card after auth.
 */
function DeviceRoute() {
  return (
    <>
      <SignedOut>
        <SignInScreen preserveCurrentUrl />
      </SignedOut>
      <SignedIn>
        <DeviceRouteHosted />
      </SignedIn>
    </>
  );
}

function DeviceRouteHosted() {
  const hub = useWebHubClient();
  return <DevicePage hub={hub} />;
}

/**
 * Shared factory for the browser HubClient. SignedInApp and DeviceRoute
 * both need an authenticated hub; pulling the setup here keeps the token
 * ref pattern (stable closure around Clerk's rotating getToken) in one
 * place.
 */
function useWebHubClient(): HubClient {
  const { getToken } = useAuth();
  const getTokenRef = useRef(getToken);
  useEffect(() => {
    getTokenRef.current = getToken;
  }, [getToken]);
  return useMemo(
    () =>
      createBrowserHubClient({
        baseUrl: config.apiBaseUrl,
        getToken: () => getTokenRef.current(),
      }),
    [],
  );
}

function SignedInApp() {
  // Hub + relay + ctx live through the lifetime of SignedInApp — they hold
  // websockets we don't want to churn on every Clerk token rotation (that
  // ref-stabilization now lives in useWebHubClient).
  const hub = useWebHubClient();
  const { getToken } = useAuth();
  const getTokenRef = useRef(getToken);
  useEffect(() => {
    getTokenRef.current = getToken;
  }, [getToken]);

  const { ctx, relay } = useMemo(() => {
    configurePeerManager(hub);
    const relayClient = createRelayClient({
      baseUrl: config.apiBaseUrl,
      getToken: () => getTokenRef.current(),
      clientType: "web",
    });
    const pty = createRelayPtyAdapter(hub, relayClient);
    return {
      ctx: { shell: createBrowserShellBase({ pty }), hub } as DataContext,
      relay: relayClient,
    };
  }, [hub]);

  return (
    <DataContextProvider ctx={ctx}>
      <Routes>
        <Route path="/" element={<IndexRedirect />} />
        <Route path="/w" element={<IndexRedirect />} />
        <Route path="/welcome" element={<WelcomeRoute />} />
        {/* One wildcard route for the whole workspace tree so sibling
            channel/agent navigation reconciles WorkspaceScreen instead of
            tearing it down. Inner view state is read from useParams below. */}
        <Route path="/w/:workspaceId/*" element={<WorkspaceScreen relay={relay} />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </DataContextProvider>
  );
}

/**
 * Resolves `/` to `/w/<active_workspace_id>` where active_workspace_id comes
 * from D1. Falls back to the first workspace if D1 has no preference, and
 * to a bare `/w` sentinel if the user has no workspaces at all.
 */
function IndexRedirect() {
  const ctx = useDataContext();
  const [destination, setDestination] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Resolve-and-maybe-bootstrap runs exactly once per mount. bootstrap's
  // check-then-insert isn't atomic (rocky), so an effect re-fire / StrictMode
  // double-invoke must never double-call it. IndexRedirect navigates away on
  // success, so once is the right semantics anyway.
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    let cancelled = false;
    void (async () => {
      try {
        let [me, workspaces] = await Promise.all([
          ctx.hub.getMe(),
          ctx.hub.listWorkspaces(),
        ]);
        // First-run (KTK-335): a brand-new user has no workspace. Auto-
        // bootstrap one (server creates it + a #general channel + sets it
        // active) so they never hit the dead `/w` sentinel. Idempotent —
        // safe against a manual-create race.
        //
        // Only a *freshly bootstrapped* user is sent to the connect-runtime
        // welcome flow. Existing users — including today's pty-daemon users,
        // who have no agent-runtime host yet during the transition — always
        // land in their workspace; we never force them through /welcome.
        let justBootstrapped = false;
        if (workspaces.length === 0) {
          const { workspace, bootstrapped } = await ctx.hub.bootstrapUser();
          workspaces = [workspace];
          me = { ...(me ?? {}), active_workspace_id: workspace.id };
          justBootstrapped = bootstrapped;
        }
        if (cancelled) return;
        const preferredId = me?.active_workspace_id ?? null;
        const hit = preferredId
          ? workspaces.find((w) => w.id === preferredId)
          : null;
        const target = hit ?? workspaces[0] ?? null;
        if (justBootstrapped) {
          setDestination("/welcome");
        } else {
          setDestination(target ? `/w/${target.id}` : "/welcome");
        }
      } catch (e: unknown) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ctx.hub]);

  if (error) {
    return (
      <CenteredMessage>
        <strong>Hub error:</strong> {error}
      </CenteredMessage>
    );
  }
  if (!destination) return <CenteredMessage>Loading workspace…</CenteredMessage>;
  return <Navigate to={destination} replace />;
}

/**
 * KTK-335 — first-run "Connect your runtime" landing. Reached when a user
 * has no runtime connected yet (incl. immediately after auto-bootstrap).
 * The panel polls for the runtime; on connect (or skip) we resolve the
 * user's active workspace and drop them into it.
 */
function WelcomeRoute() {
  const ctx = useDataContext();
  const navigate = useNavigate();

  const goToWorkspace = useCallback(() => {
    void (async () => {
      try {
        const [me, workspaces] = await Promise.all([
          ctx.hub.getMe(),
          ctx.hub.listWorkspaces(),
        ]);
        const preferredId = me?.active_workspace_id ?? null;
        const hit = preferredId
          ? workspaces.find((w) => w.id === preferredId)
          : null;
        const target = hit ?? workspaces[0] ?? null;
        navigate(target ? `/w/${target.id}` : "/", { replace: true });
      } catch {
        navigate("/", { replace: true });
      }
    })();
  }, [ctx.hub, navigate]);

  return (
    <ConnectRuntimePanel
      hub={ctx.hub}
      onCreateAgent={goToWorkspace}
      onSkip={goToWorkspace}
    />
  );
}

function WorkspaceScreen({
  relay,
}: {
  relay: ReturnType<typeof createRelayClient>;
}) {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const ctx = useDataContext();
  const navigate = useNavigate();
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Hold stable refs so the workspace-load effect only depends on
  // `workspaceId`. If ctx.hub or navigate identity ever shifts (saw this in
  // practice: a spurious re-fire nuked workspace to null mid-nav and the
  // whole shell unmounted to "Loading workspace…"), the fetch stays scoped
  // to actual workspace-id changes.
  const hubRef = useRef(ctx.hub);
  const navigateRef = useRef(navigate);
  useEffect(() => {
    hubRef.current = ctx.hub;
    navigateRef.current = navigate;
  });

  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    setError(null);
    // Only blank the workspace if we're genuinely switching to a different
    // id — refetching for the same id should hold the current one in place
    // while we confirm it's still accessible. Keeps the shell from
    // flashing "Loading workspace…" on any spurious effect re-fire.
    setWorkspace((prev) => (prev && prev.id === workspaceId ? prev : null));
    hubRef.current
      .listWorkspaces()
      .then((workspaces) => {
        if (cancelled) return;
        const hit = workspaces.find((w) => w.id === workspaceId);
        if (hit) {
          setWorkspace(hit);
        } else {
          // URL references a workspace this user no longer has access to — bounce
          // back to the index redirect so they land somewhere valid.
          navigateRef.current("/", { replace: true });
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  // Persist the active workspace back to D1 so the next `/` redirect lands here.
  useEffect(() => {
    if (!workspace) return;
    hubRef.current.setActiveWorkspace(workspace.id).catch(() => {
      // Offline or hub hiccup — URL still drives this tab's state.
    });
  }, [workspace?.id]);

  if (error) {
    return (
      <CenteredMessage>
        <strong>Hub error:</strong> {error}
      </CenteredMessage>
    );
  }
  if (!workspace) return <CenteredMessage>Loading workspace…</CenteredMessage>;

  return (
    <RelayProvider client={relay} workspaceId={workspace.id}>
      <Shell relay={relay} workspace={workspace} />
    </RelayProvider>
  );
}

function Shell({
  relay,
  workspace,
}: {
  relay: ReturnType<typeof createRelayClient>;
  workspace: Workspace;
}) {
  // Sub-routes are matched relative to the wildcard parent (`/w/:wsId/*`).
  // useMatch returns null when the pattern doesn't match the current URL.
  const channelMatch = useMatch("/w/:workspaceId/c/:channelId");
  const agentMatch = useMatch("/w/:workspaceId/a/:agentId");
  // KTK-259 — workspace settings is a tabbed full-page view at
  // /w/:wsId/settings/:tab (profile | integrations | agent-defaults).
  // The :tab segment is part of the path so the URL is shareable; clicking the
  // sidebar item lands you on /settings/profile.
  const settingsMatch = useMatch("/w/:workspaceId/settings/:tab");
  // KTK-303 — Knowledge view is a sibling top-level route at
  // /w/:wsId/knowledge/:tab (vault | journalists). Vault tab is the default.
  const knowledgeMatch = useMatch("/w/:workspaceId/knowledge/:tab");
  // KTK-303 — vault deep-link route. `/w/:wsId/v/*` is the URL form Hub's
  // `knowledge_query` rewrites internal vault references into; landing here
  // opens the Knowledge view's Vault tab with that file selected.
  const vaultDeepMatch = useMatch("/w/:workspaceId/v/*");
  const channelId = channelMatch?.params.channelId;
  const agentId = agentMatch?.params.agentId;
  const settingsTab = settingsMatch?.params.tab as WorkspaceSettingsTab | undefined;
  const knowledgeTab = knowledgeMatch?.params.tab as WorkspaceKnowledgeTab | undefined;
  const vaultDeepPath = vaultDeepMatch?.params["*"] ?? null;
  const knowledgeOpen = !!knowledgeTab || vaultDeepPath !== null;
  const effectiveKnowledgeTab: WorkspaceKnowledgeTab | null = vaultDeepPath
    ? "vault"
    : knowledgeTab ?? null;
  // Vault deep-links arrive as `/v/<layer>/<slug>` (no `.md`). The viewer
  // resolves the trailing `.md`, so we just pass the bare path through.
  const initialVaultPath = vaultDeepPath ? `${vaultDeepPath}.md` : undefined;
  const { user } = useUser();
  const ctx = useDataContext();
  const navigate = useNavigate();
  const relayCtx = useRelay();
  void relay;
  const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({});
  const [mentionCounts, setMentionCounts] = useState<Record<string, number>>({});
  // Bumped on `decision_*` relay broadcasts → forces DecisionsSidebar to
  // re-list pending decisions and Shell's `channels` cache to re-fetch
  // (which carries the fresh `pending_decisions_count` per channel).
  const [decisionsRefetchKey, setDecisionsRefetchKey] = useState(0);
  const [draftChannelIds, setDraftChannelIds] = useState<Set<string>>(new Set());
  const [showCreateChannel, setShowCreateChannel] = useState(false);
  // KTK-317 — right-click → Edit channel. `contextMenu` carries the
  // anchor coordinates + the channel the cursor was over; `editChannel`
  // is the channel currently open in the edit modal (drives mount /
  // dismiss). One pair of states services both sidebar Channels +
  // Editorial lists since each row routes through the same handler.
  const [channelContextMenu, setChannelContextMenu] = useState<{
    channel: Channel;
    x: number;
    y: number;
  } | null>(null);
  const [editChannel, setEditChannel] = useState<Channel | null>(null);
  const [channelReloadToken, setChannelReloadToken] = useState(0);
  const [workspaceReloadToken, setWorkspaceReloadToken] = useState(0);
  // KTK-259 v2 — Workspace settings is now a full-page route, not a stack of
  // modals. The sidebar item navigates to /w/:wsId/settings/profile and the
  // four tabs (Profile / Knowledge / Integrations / Agent defaults) are
  // segments of the URL.
  const [showAgentSettings, setShowAgentSettings] = useState(false);
  const [showCreateAgent, setShowCreateAgent] = useState(false);
  const [agentReloadToken, setAgentReloadToken] = useState(0);
  // Transient "Starting…"/"Stopping…" label for the Start/Stop button. The
  // daemon's presence poll (5s) closes this out on success; we fall back to
  // a 10s timer to unstick the label if nothing ever shows up (e.g. start
  // silently failed on the host).
  const [agentPendingOp, setAgentPendingOp] = useState<{
    agentId: string;
    kind: "start" | "stop";
  } | null>(null);
  const [agentOpError, setAgentOpError] = useState<string | null>(null);
  const [customHandle, setCustomHandle] = useState<string | null>(null);
  const [customDisplayName, setCustomDisplayName] = useState<string | null>(null);
  const authorName =
    user?.fullName ?? user?.primaryEmailAddress?.emailAddress ?? "you";
  const effectiveHandle = customHandle ?? authorName;
  const effectiveDisplayName = customDisplayName ?? authorName;
  const [listColWidth, setListColWidth] = usePersistedWidth(
    "kritaka:web:colWidth:lists",
    260,
  );

  // Cache the workspace's channels + agents at the Shell level, fetched
  // ONCE on workspace mount (plus a slow background refresh for the agent
  // connected state). Without this cache the previous impl re-fetched on
  // every channel/agent click, which made the main pane flash "Loading…"
  // for a few hundred ms before the pane swap — felt like the whole app
  // was rebuilding.
  const [channels, setChannels] = useState<Channel[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);

  // Each Agent V2 row carries a `dm_channel_id` for its 1:1 DM with the
  // workspace owner — that channel is the backing store for the agent's
  // `/a/<id>` chat surface, so it shouldn't also appear as a peer entry
  // in the regular channel tree (Akari flagged dm-dino leaking into
  // Editorial). Compute the set so ChannelList can exclude it.
  const agentDmChannelIds = useMemo(() => {
    const s = new Set<string>();
    for (const a of agents) {
      if (a.dm_channel_id) s.add(a.dm_channel_id);
    }
    return s;
  }, [agents]);

  useEffect(() => {
    let cancelled = false;
    listChannels(ctx, workspace.id).then((cs) => {
      if (!cancelled) setChannels(cs);
    });
    const loadAgents = () => {
      listAgents(ctx, workspace.id).then((a) => {
        if (!cancelled) setAgents(a);
      });
    };
    loadAgents();
    const timer = setInterval(loadAgents, 15_000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [ctx, workspace.id, channelReloadToken, agentReloadToken]);

  // Live agent presence map. Keys are agent ids; value records the per-agent
  // PTY state we surface in AgentList:
  //   - "active": Hub says the agent is live AND some daemon has emitted
  //     PTY output for it in the last ACTIVE_MS.
  //   - "idle":   Hub says the agent is live but there's no recent output.
  //   - (absent): Hub says the agent isn't live → AgentList falls back to
  //     `last_seen_at` / `agent.status`.
  //
  // Authoritative `live` comes from `/api/workspaces/:ws/agents/presence`
  // (DO-backed, computed from `sessionToHost` + the daemon registry).
  // `listSessions` is only used to extract `lastOutputAt` for the
  // active/idle nuance; if it's slow or empty we still paint correctly
  // because the outer loop is gated on the presence endpoint. The old
  // `PRESENCE_MISS_TOLERANCE` sticky window stays for network hiccups.
  const [agentPresence, setAgentPresence] = useState<Map<string, "active" | "idle">>(
    new Map(),
  );
  // KTK-225: parallel map for Agent V2 worker status, populated from
  // the same /presence response. Carries runtime-broadcast status +
  // last_error so the Worker pane can render `error` distinctly from
  // `stopped` and surface the failure reason in a tooltip.
  const [v2WorkerStatus, setV2WorkerStatus] = useState<
    Map<string, { status: "running" | "stopped" | "error"; lastError: string | null }>
  >(new Map());
  const presenceMissesRef = useRef<Map<string, number>>(new Map());
  // KTK-335 — owner-scoped agent-runtime hosts for the Runtimes sidebar.
  // Fetched in the same presence poll below (no second interval). The pip
  // keys off last_seen_at once rocky's relay lane (KTK-339) stamps it.
  const [runtimeHosts, setRuntimeHosts] = useState<AgentRuntimeHost[]>([]);
  const refreshAgentPresence = useCallback(async () => {
    const ACTIVE_MS = 5_000;
    const PRESENCE_MISS_TOLERANCE = 2;
    const pty = ctx.shell.pty;
    try {
      // KTK-374 — one broadcast instead of a per-online-host fan-out.
      // `session:list` reaches every connected host (pty daemon or agent
      // runtime) regardless of the host list, and the old loop keyed off
      // pty hosts only: presence for runtime-hosted agents already went
      // dark whenever no pty daemon was online, and would have gone dark
      // permanently at daemon retirement.
      const [presence, sessions, runtimeHostList] = await Promise.all([
        ctx.hub.listAgentPresence(workspace.id),
        pty.listSessions().catch(() => [] as Awaited<ReturnType<typeof pty.listSessions>>),
        // KTK-335 — owner-scoped V2 runtimes for the sidebar; null on a
        // transient error keeps the prior list rather than blanking it.
        ctx.hub.listAgentRuntimeHosts().catch(() => null),
      ]);
      if (runtimeHostList !== null) setRuntimeHosts(runtimeHostList);
      const lastOutputByAgent = new Map<string, string>();
      for (const s of sessions) {
        if (!s.agentId || !s.lastOutputAt) continue;
        lastOutputByAgent.set(s.agentId, s.lastOutputAt);
      }
      const observed = new Map<string, "active" | "idle">();
      const v2 = new Map<string, { status: "running" | "stopped" | "error"; lastError: string | null }>();
      const now = Date.now();
      for (const [agentId, info] of Object.entries(presence)) {
        // KTK-225: capture v2 status + last_error regardless of `live`
        // (a stopped/errored v2 agent is still an interesting state to
        // surface). For Claude Code rows these fields are undefined.
        if (info.runtime_kind === "agent-v2" && info.status) {
          v2.set(agentId, { status: info.status, lastError: info.last_error ?? null });
        }
        // KTK-225: light up the sidebar pip for v2 agents whose worker
        // is running, regardless of the Claude-Code-shaped `live`
        // flag. There's no PTY session for v2, so PTY-based
        // active/idle distinction doesn't apply — running ⇒ idle is
        // the right floor (next message kicks a turn → active).
        if (info.runtime_kind === "agent-v2") {
          if (info.status === "running") observed.set(agentId, "idle");
          continue;
        }
        if (!info.live) continue;
        const lastOutput = lastOutputByAgent.get(agentId);
        const outputAt = lastOutput ? Date.parse(lastOutput) : null;
        const isActive = outputAt !== null && now - outputAt < ACTIVE_MS;
        observed.set(agentId, isActive ? "active" : "idle");
      }
      setV2WorkerStatus(v2);
      setAgentPresence((prev) => {
        const next = new Map<string, "active" | "idle">(observed);
        const missCounts = presenceMissesRef.current;
        for (const [agentId, state] of prev) {
          if (observed.has(agentId)) {
            missCounts.delete(agentId);
            continue;
          }
          const misses = (missCounts.get(agentId) ?? 0) + 1;
          if (misses >= PRESENCE_MISS_TOLERANCE) {
            missCounts.delete(agentId);
          } else {
            missCounts.set(agentId, misses);
            next.set(agentId, state);
          }
        }
        return next;
      });
    } catch {
      /* swallow — stale map stays */
    }
  }, [ctx, workspace.id]);
  useEffect(() => {
    void refreshAgentPresence();
    const timer = setInterval(() => void refreshAgentPresence(), 5_000);
    return () => clearInterval(timer);
  }, [refreshAgentPresence]);

  const channel = channelId ? channels.find((c) => c.id === channelId) ?? null : null;
  const agent = agentId ? agents.find((a) => a.id === agentId) ?? null : null;

  // Clear a pending start/stop once presence catches up, or after a 10s
  // safety timeout so the button doesn't stick forever if something on the
  // daemon silently failed.
  useEffect(() => {
    if (!agentPendingOp) return;
    const { agentId: pendingId, kind } = agentPendingOp;
    const isPresent = agentPresence.has(pendingId);
    if (kind === "start" && isPresent) {
      setAgentPendingOp(null);
      setAgentReloadToken((n) => n + 1);
      return;
    }
    if (kind === "stop" && !isPresent) {
      setAgentPendingOp(null);
      setAgentReloadToken((n) => n + 1);
      return;
    }
    const t = setTimeout(() => {
      setAgentPendingOp(null);
      setAgentReloadToken((n) => n + 1);
    }, 10_000);
    return () => clearTimeout(t);
  }, [agentPendingOp, agentPresence]);

  const startSelectedAgent = useCallback(
    async (withContinue: boolean) => {
      if (!agent) return;
      setAgentOpError(null);
      setAgentPendingOp({ agentId: agent.id, kind: "start" });
      try {
        await ctx.hub.startAgent(workspace.id, agent.id, { continue: withContinue });
        // Force an immediate presence re-fetch so the pending label, the
        // button swap, and AgentTerminal's locate/attach all land in ~1s
        // instead of waiting up to the next 5s poll tick.
        void refreshAgentPresence();
      } catch (e: unknown) {
        setAgentOpError(e instanceof Error ? e.message : String(e));
        setAgentPendingOp(null);
      }
    },
    [agent, ctx.hub, workspace.id, refreshAgentPresence],
  );

  const stopSelectedAgent = useCallback(async () => {
    if (!agent) return;
    setAgentOpError(null);
    setAgentPendingOp({ agentId: agent.id, kind: "stop" });
    try {
      await ctx.hub.stopAgent(workspace.id, agent.id);
      void refreshAgentPresence();
    } catch (e: unknown) {
      setAgentOpError(e instanceof Error ? e.message : String(e));
      setAgentPendingOp(null);
    }
  }, [agent, ctx.hub, workspace.id, refreshAgentPresence]);

  // Bounce the URL back to the workspace root if it references an id that
  // isn't in the loaded list — covers deleted channels / no-access agents.
  useEffect(() => {
    if (channelId && channels.length > 0 && !channels.some((c) => c.id === channelId)) {
      navigate(`/w/${workspace.id}`, { replace: true });
    }
  }, [channelId, channels, navigate, workspace.id]);

  useEffect(() => {
    if (agentId && agents.length > 0 && !agents.some((a) => a.id === agentId)) {
      navigate(`/w/${workspace.id}`, { replace: true });
    }
  }, [agentId, agents, navigate, workspace.id]);

  // Load the user's handle + display name from workspace settings.
  // - userHandle drives @-mentions / owner-mention styling (e.g. "akari").
  // - displayName is what gets stamped on outgoing messages and shown in
  //   the message author header (e.g. "Akari Lara").
  // Both fall back to the Clerk display name when unset.
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      ctx.hub.getWorkspaceSetting(workspace.id, "userHandle"),
      ctx.hub.getWorkspaceSetting(workspace.id, "displayName"),
    ])
      .then(([h, d]) => {
        if (cancelled) return;
        setCustomHandle(typeof h === "string" && h ? h : null);
        setCustomDisplayName(typeof d === "string" && d ? d : null);
      })
      .catch(() => {
        if (cancelled) return;
        setCustomHandle(null);
        setCustomDisplayName(null);
      });
    return () => {
      cancelled = true;
    };
  }, [ctx.hub, workspace.id]);

  // Refresh draft indicators when the workspace loads or the user navigates
  // away from a channel (ChannelView debounces its persists; we catch up here).
  useEffect(() => {
    let cancelled = false;
    listDrafts(ctx, workspace.id).then((drafts) => {
      if (cancelled) return;
      setDraftChannelIds(new Set(drafts.map((d) => d.viewId)));
    });
    return () => {
      cancelled = true;
    };
  }, [ctx, workspace.id, channelId]);

  // Ask for notification permission once, at workspace load time.
  useEffect(() => {
    void ctx.shell.notifications.requestPermission();
  }, [ctx]);

  // Track unread + mention counts per-channel from relay messages, and fire
  // a desktop notification on mentions of the signed-in user.
  useEffect(() => {
    const escaped = effectiveHandle.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const mentionRegex = new RegExp(`@${escaped}\\b`);
    return relayCtx.onMessage((msg) => {
      if (msg.type !== "channel_message" || !msg.channel_id) return;
      if (msg.channel_id === channelId) return;
      setUnreadCounts((prev) => ({
        ...prev,
        [msg.channel_id!]: (prev[msg.channel_id!] ?? 0) + 1,
      }));
      if (typeof msg.content === "string" && mentionRegex.test(msg.content)) {
        setMentionCounts((prev) => ({
          ...prev,
          [msg.channel_id!]: (prev[msg.channel_id!] ?? 0) + 1,
        }));
        playMentionSound();
        void ctx.shell.notifications.notify({
          title:
            typeof msg.author_name === "string"
              ? `${msg.author_name} mentioned you`
              : "New mention",
          body: typeof msg.content === "string" ? msg.content : "",
          tag: `kritaka-mention-${msg.channel_id}`,
          // We play the TNG bell ourselves; suppress the OS default tone
          // so the user doesn't hear two stacked sounds.
          silent: true,
          onClick: () => {
            if (msg.channel_id) {
              navigate(`/w/${workspace.id}/c/${msg.channel_id}`);
            }
          },
        });
      }
    });
  }, [relayCtx, ctx, effectiveHandle, channelId, workspace.id, navigate]);

  // KTK-317 — cross-tab sync for channel edits (rename, description,
  // subscriber changes). Hub broadcasts a `channel_updated` frame
  // through the workspace's relay DO after each PATCH / PUT; every
  // connected client bumps its channel-reload token so the sidebar
  // lists re-fetch and the renamed row picks up the new label without
  // a manual refresh.
  useEffect(() => {
    return relayCtx.onMessage((msg) => {
      if (msg.type !== "channel_updated") return;
      setChannelReloadToken((n) => n + 1);
    });
  }, [relayCtx]);

  // KTK-318 — cross-tab sync for read-state. Hub fans out a
  // `read_state_updated` frame *per-user* (not per-workspace) after the
  // read POST below lands. Routing-field name is `target_user_id`
  // (rocky's relay convention for "private to one human" signals). The
  // semantic is "this user read this channel → clear to 0"; no
  // unread_count rides the frame because read==cleared is the only
  // state the backend tracks today. workspace_id is double-checked
  // because the socket is already workspace-scoped — belt and braces.
  useEffect(() => {
    const selfUserId = user?.id;
    return relayCtx.onMessage((msg) => {
      if (msg.type !== "read_state_updated") return;
      if (typeof msg.target_user_id === "string" && selfUserId && msg.target_user_id !== selfUserId) return;
      if (typeof msg.workspace_id === "string" && msg.workspace_id !== workspace.id) return;
      const ch = typeof msg.channel_id === "string" ? msg.channel_id : null;
      if (!ch) return;
      // Mirror the local "read it → clear both" behavior (see the
      // current-channel effect below). Mention counts aren't in the
      // frame, but reading the channel is the only thing that clears
      // them today, so it's safe to clear both here. Idempotent —
      // the originating tab also receives this frame and re-runs a
      // no-op clear.
      setUnreadCounts((prev) => {
        if (!(ch in prev)) return prev;
        const next = { ...prev };
        delete next[ch];
        return next;
      });
      setMentionCounts((prev) => {
        if (!(ch in prev)) return prev;
        const next = { ...prev };
        delete next[ch];
        return next;
      });
    });
  }, [relayCtx, user?.id, workspace.id]);

  // Clear unread/mention counts for the currently-viewed channel, and
  // fire the cross-tab `read_state_updated` fanout so other tabs of
  // the same user converge. The POST itself doesn't persist server-
  // side (rocky's note); its only job is to trigger the relay
  // broadcast. Best-effort — a transient failure leaves the local
  // clear in place, just no cross-tab sync that pass.
  useEffect(() => {
    if (!channelId) return;
    setUnreadCounts((prev) => {
      if (!prev[channelId]) return prev;
      const next = { ...prev };
      delete next[channelId];
      return next;
    });
    setMentionCounts((prev) => {
      if (!prev[channelId]) return prev;
      const next = { ...prev };
      delete next[channelId];
      return next;
    });
    void ctx.hub
      .markChannelRead(workspace.id, channelId, {
        last_read_at: new Date().toISOString(),
      })
      .catch(() => {
        // Swallow — fanout is best-effort.
      });
  }, [channelId, ctx.hub, workspace.id]);

  // Reset counts on workspace change — different set of channels.
  useEffect(() => {
    setUnreadCounts({});
    setMentionCounts({});
  }, [workspace.id]);

  // Decision-relay listener. Each broadcast carries the fresh
  // `pending_decisions_count` joined into the next channels-list response,
  // so we just kick the cache: `channelReloadToken` re-fetches Shell's
  // channels, `decisionsRefetchKey` re-fetches the sidebar list.
  useEffect(() => {
    return relayCtx.onMessage((msg) => {
      if (
        msg.type === "decision_created" ||
        msg.type === "decision_answered" ||
        msg.type === "decision_cancelled"
      ) {
        setChannelReloadToken((t) => t + 1);
        setDecisionsRefetchKey((k) => k + 1);
      }
    });
  }, [relayCtx]);

  // Active-workspace pending count, summed from `pending_decisions_count`
  // joined into the channels-list response. Drives the right-rail
  // DecisionsSidebar (which is intentionally workspace-scoped — Akari only
  // wants to see and resolve decisions from the workspace they're currently
  // looking at).
  const totalPendingDecisions = channels.reduce(
    (n, c) => n + (c.pending_decisions_count ?? 0),
    0,
  );

  // KTK-260 — cross-workspace pending counts. Backs the per-tile badge on
  // WorkspaceList (every workspace, not just the active one) and the
  // `(N) Kritaka` document.title prefix that aggregates across every
  // workspace this tab is signed into.
  //
  // The hook polls every 30s + refetches on relay decision events, but
  // those events are workspace-scoped so the *active* workspace's count
  // can lag the freshly-mutated `channels` array by one tick. Override
  // the active entry with the live channels-derived count so the tile
  // badge and tab title update instantly when decisions change here.
  const { pendingByWorkspace } = usePendingDecisionsByWorkspace(
    decisionsRefetchKey,
    workspaceReloadToken,
  );
  const mergedPendingByWorkspace = useMemo(
    () => ({ ...pendingByWorkspace, [workspace.id]: totalPendingDecisions }),
    [pendingByWorkspace, workspace.id, totalPendingDecisions],
  );
  const totalPendingAcrossWorkspaces = useMemo(
    () => Object.values(mergedPendingByWorkspace).reduce((a, b) => a + b, 0),
    [mergedPendingByWorkspace],
  );

  useEffect(() => {
    const base = `Kritaka — ${workspace.name}`;
    document.title =
      totalPendingAcrossWorkspaces > 0
        ? `(${totalPendingAcrossWorkspaces}) ${base}`
        : base;
    return () => {
      document.title = "Kritaka";
    };
  }, [totalPendingAcrossWorkspaces, workspace.name]);

  const goToWorkspace = useCallback(
    (w: Workspace) => {
      navigate(`/w/${w.id}`);
    },
    [navigate],
  );

  const goToChannel = useCallback(
    (c: Channel) => {
      navigate(`/w/${workspace.id}/c/${c.id}`);
    },
    [navigate, workspace.id],
  );

  const goToAgent = useCallback(
    (a: Agent) => {
      navigate(`/w/${workspace.id}/a/${a.id}`);
    },
    [navigate, workspace.id],
  );

  // KTK-397 — workspace-wide active threads for the sidebar. Refreshed on a
  // slow poll plus thread lifecycle frames; grouped by channel for
  // ChannelList. Degrades loudly to empty on a Hub predating the endpoint.
  const [workspaceThreads, setWorkspaceThreads] = useState<Thread[]>([]);
  const [pendingThread, setPendingThread] = useState<Thread | null>(null);
  const refreshWorkspaceThreads = useCallback(async () => {
    try {
      setWorkspaceThreads(await ctx.hub.listWorkspaceThreads(workspace.id));
    } catch (e) {
      console.warn("[App] listWorkspaceThreads failed; sidebar shows no threads:", e);
      setWorkspaceThreads([]);
    }
  }, [ctx.hub, workspace.id]);
  useEffect(() => {
    void refreshWorkspaceThreads();
    const t = setInterval(() => void refreshWorkspaceThreads(), 60_000);
    return () => clearInterval(t);
  }, [refreshWorkspaceThreads]);
  useEffect(() => {
    return relayCtx.onMessage((msg) => {
      if (msg.type === "thread_created" || msg.type === "thread_updated") {
        void refreshWorkspaceThreads();
      }
    });
  }, [relayCtx, refreshWorkspaceThreads]);
  const threadsByChannel = useMemo(() => {
    const map: Record<string, Thread[]> = {};
    for (const t of workspaceThreads) {
      (map[t.channel_id] ??= []).push(t);
    }
    return map;
  }, [workspaceThreads]);
  const goToThread = useCallback(
    (c: Channel, t: Thread) => {
      setPendingThread(t);
      navigate(`/w/${workspace.id}/c/${c.id}`);
    },
    [navigate, workspace.id],
  );

  return (
    <div
      style={{
        height: "100vh",
        width: "100%",
        display: "grid",
        gridTemplateRows: "auto 1fr",
      }}
    >
      {/* Top nav (KTK-274) — user-level chrome only, ported from Claude
       *  Design's web-shell artboards.
       *    Left:  Kritaka wordmark + logo. The Latin "kritaka" cube-flips
       *           per syllable on hover into its Devanagari akṣara कृ-त-क.
       *    Right: `@<handle>` chip showing the user's per-workspace handle,
       *           then the Clerk user menu (avatar). Workspace config is in
       *           the workspace-named sidebar collapsible (col 2). */}
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          height: 36,
          padding: "0 14px 0 16px",
          borderBottom: "1px solid var(--hairline)",
          background: "var(--paper)",
          flexShrink: 0,
        }}
      >
        <KritakaWordmark size="sm" />
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            color: "var(--ink-3)",
            fontSize: 13,
          }}
        >
          <span
            title="Your handle in this workspace"
            style={{
              fontFamily: "var(--mono)",
              fontSize: 11,
              color: "var(--ink-3)",
              letterSpacing: "-0.01em",
              whiteSpace: "nowrap",
            }}
          >
            <span style={{ color: "var(--ink-4)" }}>@</span>
            {effectiveHandle}
          </span>
          <ThemeToggle />
          <UserButton />
        </div>
      </header>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `${WORKSPACE_COLUMN_WIDTH}px ${listColWidth}px 4px 1fr ${
            totalPendingDecisions > 0 ? "320px" : "0px"
          }`,
          overflow: "hidden",
          transition: "grid-template-columns 0.15s ease",
        }}
      >
        <aside
          style={{
            padding: "8px 0 12px",
            overflowY: "auto",
            background: "var(--paper-2)",
            borderRight: "1px solid var(--hairline)",
          }}
        >
          <WorkspaceList
            selectedWorkspaceId={workspace.id}
            onSelect={goToWorkspace}
            reloadToken={workspaceReloadToken}
            pendingByWorkspace={mergedPendingByWorkspace}
          />
        </aside>
        <aside
          style={{
            padding: "4px 0 16px",
            overflowY: "auto",
            background: "var(--paper-2)",
            display: "flex",
            flexDirection: "column",
            gap: 0,
          }}
        >
          {/* Workspace-config (KTK-259, restructured in KTK-303) — section
           *  now mirrors the icon+label row pattern of Channels/Agents/etc.
           *  Two entries: Settings (Profile / Integrations / Agent defaults
           *  page) and Knowledge (Vault / Journalists page). Section title
           *  is the workspace name. */}
          <CollapsibleSection
            label={workspace.name}
            storageKey={`workspace-config-${workspace.id}`}
          >
            <WorkspaceConfigItem
              icon={<GearIcon />}
              selected={!!settingsTab}
              onClick={() => navigate(`/w/${workspace.id}/settings/profile`)}
            >
              Settings
            </WorkspaceConfigItem>
            <WorkspaceConfigItem
              icon={<BookIcon />}
              selected={knowledgeOpen}
              onClick={() => navigate(`/w/${workspace.id}/knowledge/vault`)}
            >
              Knowledge
            </WorkspaceConfigItem>
          </CollapsibleSection>
          {/* KTK-335 — Runtimes: the user's connected agent runtimes
           *  (owner-scoped, shown across every workspace). Pip is green once
           *  the runtime has connected (last_seen_at, via rocky's KTK-339),
           *  grey when registered-only. "+" / empty row routes to the
           *  guided connect-runtime flow. */}
          <CollapsibleSection
            label="Runtimes"
            storageKey="runtimes"
            actions={
              <button
                type="button"
                onClick={() => navigate("/welcome")}
                title="Connect a runtime"
                style={{
                  background: "none",
                  border: "none",
                  color: "var(--text-muted)",
                  cursor: "pointer",
                  fontSize: 16,
                  lineHeight: 1,
                  padding: "0 4px",
                }}
              >
                +
              </button>
            }
          >
            {runtimeHosts.length === 0 ? (
              <button
                type="button"
                onClick={() => navigate("/welcome")}
                style={{
                  width: "100%",
                  textAlign: "left",
                  padding: "4px 8px 4px 22px",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  fontSize: 13,
                  color: "var(--ink-3)",
                  minHeight: 24,
                }}
              >
                Connect a runtime…
              </button>
            ) : (
              runtimeHosts.map((h) => (
                <div
                  key={h.host_id}
                  title={`${h.display_name}${h.hostname ? ` · ${h.hostname}` : ""} — ${
                    h.last_seen_at ? "connected" : "registered"
                  }${h.claude_logged_in === false ? " · Claude not signed in" : ""}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "4px 8px 4px 22px",
                    fontSize: 13,
                    color: "var(--ink-2)",
                    minHeight: 24,
                  }}
                >
                  <span
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      flexShrink: 0,
                      background: h.last_seen_at ? "var(--success)" : "var(--ink-3)",
                    }}
                  />
                  <span
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {h.display_name}
                  </span>
                  {h.claude_logged_in === false && (
                    <span
                      aria-label="Claude not signed in on this machine"
                      title="Claude not signed in on this machine — sign into Claude there so your agents can run"
                      style={{ marginLeft: 4, color: "var(--error)", flexShrink: 0 }}
                    >
                      ⚠
                    </span>
                  )}
                </div>
              ))
            )}
          </CollapsibleSection>
          <CollapsibleSection
            label="Channels"
            storageKey="channels"
            actions={
              <button
                type="button"
                onClick={() => setShowCreateChannel(true)}
                title="New channel"
                style={{
                  background: "none",
                  border: "none",
                  color: "var(--text-muted)",
                  cursor: "pointer",
                  fontSize: 16,
                  padding: 0,
                  lineHeight: 1,
                }}
              >
                +
              </button>
            }
          >
            <ChannelList
              key={channelReloadToken}
              workspaceId={workspace.id}
              selectedChannelId={channelId ?? null}
              onSelect={goToChannel}
              unreadCounts={unreadCounts}
              mentionCounts={mentionCounts}
              draftChannelIds={draftChannelIds}
              filter="public"
              excludeChannelIds={agentDmChannelIds}
              onContextRequest={(channel, x, y) =>
                setChannelContextMenu({ channel, x, y })
              }
              threadsByChannel={threadsByChannel}
              selectedThreadId={pendingThread?.id ?? null}
              onSelectThread={goToThread}
            />
          </CollapsibleSection>
          <CollapsibleSection label="Scouts" storageKey="scouts">
            <ScoutList
              workspaceId={workspace.id}
              selectedChannelId={channelId ?? null}
              onSelect={goToChannel}
            />
          </CollapsibleSection>
          <CollapsibleSection
            label="Agents"
            storageKey="agents-default"
            actions={
              <button
                type="button"
                onClick={() => setShowCreateAgent(true)}
                title="New agent"
                style={{
                  background: "none",
                  border: "none",
                  color: "var(--text-muted)",
                  cursor: "pointer",
                  fontSize: 16,
                  padding: 0,
                  lineHeight: 1,
                }}
              >
                +
              </button>
            }
          >
            <AgentList
              key={`agents-${agentReloadToken}`}
              workspaceId={workspace.id}
              selectedAgentId={agentId ?? null}
              onSelect={goToAgent}
              filter="agents"
              agentPresence={agentPresence}
            />
          </CollapsibleSection>
          <CollapsibleSection label="Bullpen" storageKey="agents-editors">
            <AgentList
              workspaceId={workspace.id}
              selectedAgentId={agentId ?? null}
              onSelect={goToAgent}
              filter="editors"
              agentPresence={agentPresence}
            />
          </CollapsibleSection>
          <CollapsibleSection label="Editorial" storageKey="channels-bullpen">
            <ChannelList
              key={`bullpen-${channelReloadToken}`}
              workspaceId={workspace.id}
              selectedChannelId={channelId ?? null}
              onSelect={goToChannel}
              unreadCounts={unreadCounts}
              mentionCounts={mentionCounts}
              draftChannelIds={draftChannelIds}
              filter="bullpen"
              excludeChannelIds={agentDmChannelIds}
              onContextRequest={(channel, x, y) =>
                setChannelContextMenu({ channel, x, y })
              }
            />
          </CollapsibleSection>
        </aside>
        <ColumnResizer
          onResize={(delta: number) =>
            setListColWidth((w) => clamp(w + delta, 180, 500))
          }
        />
        <main style={{ overflow: "hidden", display: "flex", flexDirection: "column" }}>
          {settingsTab && (
            <WorkspaceSettingsPage
              workspaceId={workspace.id}
              workspaceName={workspace.name}
              activeTab={settingsTab}
              onTabChange={(tab: WorkspaceSettingsTab) =>
                navigate(`/w/${workspace.id}/settings/${tab}`)
              }
              onHandleChange={(h: string) => setCustomHandle(h || null)}
              onIconChange={() => setWorkspaceReloadToken((t) => t + 1)}
            />
          )}
          {!settingsTab && knowledgeOpen && effectiveKnowledgeTab && (
            <WorkspaceKnowledgePage
              workspaceId={workspace.id}
              workspaceName={workspace.name}
              activeTab={effectiveKnowledgeTab}
              onTabChange={(tab: WorkspaceKnowledgeTab) =>
                navigate(`/w/${workspace.id}/knowledge/${tab}`)
              }
              vaultPath={initialVaultPath}
            />
          )}
          {!settingsTab && !knowledgeOpen && channelId && channel &&
            (channel.scout_instance_id ? (
              <ScoutView
                instanceId={channel.scout_instance_id}
                streamChannel={{
                  id: channel.id,
                  name: channel.name,
                  description: channel.description,
                }}
                workspaceId={workspace.id}
                authorName={effectiveHandle}
                fallbackDisplayName={channel.name}
                onLeaseReleased={() =>
                  navigate(`/w/${workspace.id}`, { replace: true })
                }
              />
            ) : (
              <ChannelView
                workspaceId={workspace.id}
                channelId={channel.id}
                channelName={channel.name}
                channelDescription={channel.description}
                authorName={effectiveHandle}
                displayName={effectiveDisplayName}
                authorId={user?.id ?? null}
                openThreadRequest={pendingThread}
              />
            ))}
          {!settingsTab && !knowledgeOpen && agentId && agent && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                minHeight: 0,
              }}
            >
              {/*
                Toolbar (status + Start/Continue/Stop + Settings gear) is
                Claude-Code-specific — Agent V2 has its own model badge +
                inline settings panel inside AgentV2ChatView, and the
                runtime owns its lifecycle so there's no PTY to start/stop.
                Akari greenlit Option E: inline panel is canonical for v2,
                top-right gear stays for Claude Code only.
              */}
              {agent.runtime_kind !== "agent-v2" && (
              <div
                style={{
                  padding: "12px 24px",
                  borderBottom: "1px solid var(--border)",
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  flexShrink: 0,
                  background: "var(--bg-secondary)",
                }}
              >
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: 14,
                    color: "var(--text-primary)",
                  }}
                >
                  🤖 {agent.name}
                </span>
                <span style={{ fontSize: 11, color: "var(--text-muted)" }}>
                  {agent.model}
                </span>
                {(() => {
                  // Presence is the authoritative "is the PTY alive right now"
                  // signal — the agents table's `status` column lags until the
                  // daemon reports in. Pending ops pick the label; color
                  // tracks the current runningNow state so the chip doesn't
                  // go muted while the button is in its mid-op variant.
                  const pending = agentPendingOp?.agentId === agent.id ? agentPendingOp.kind : null;
                  const runningNow = agentPresence.has(agent.id);
                  const label = pending === "start"
                    ? "starting…"
                    : pending === "stop"
                    ? "stopping…"
                    : runningNow
                    ? "running"
                    : agent.status === "error"
                    ? "error"
                    : "stopped";
                  const color = pending
                    ? "var(--text-muted)"
                    : runningNow
                    ? "var(--success)"
                    : agent.status === "error"
                    ? "var(--error)"
                    : "var(--text-muted)";
                  return (
                    <span
                      style={{
                        marginLeft: "auto",
                        fontSize: 11,
                        color,
                        textTransform: "uppercase",
                      }}
                    >
                      {label}
                    </span>
                  );
                })()}
                {(() => {
                  const pending = agentPendingOp?.agentId === agent.id ? agentPendingOp.kind : null;
                  const runningNow = agentPresence.has(agent.id);
                  // During a pending op, keep showing the "from" state's
                  // buttons (not the target state), so the user sees the
                  // button they just clicked go into its pending variant
                  // rather than the UI swapping out from under them.
                  const showRunning =
                    pending === "stop"
                      ? true
                      : pending === "start"
                      ? false
                      : runningNow;
                  const disabled = pending !== null;
                  if (showRunning) {
                    return (
                      <button
                        type="button"
                        onClick={stopSelectedAgent}
                        disabled={disabled}
                        title="Stop agent"
                        style={{
                          fontSize: 11,
                          padding: "4px 12px",
                          borderRadius: 4,
                          background: "var(--bg-tertiary)",
                          color: "var(--text-secondary)",
                          border: "1px solid var(--border)",
                          cursor: disabled ? "not-allowed" : "pointer",
                          fontFamily: "inherit",
                          opacity: disabled ? 0.6 : 1,
                        }}
                      >
                        {pending === "stop" ? "Stopping…" : "Stop"}
                      </button>
                    );
                  }
                  return (
                    <div style={{ display: "flex", gap: 4 }}>
                      <button
                        type="button"
                        onClick={() => void startSelectedAgent(false)}
                        disabled={disabled}
                        title="Start fresh (no --continue)"
                        style={{
                          fontSize: 11,
                          padding: "4px 12px",
                          borderRadius: 4,
                          background: "var(--bg-tertiary)",
                          color: "var(--text-secondary)",
                          border: "1px solid var(--border)",
                          cursor: disabled ? "not-allowed" : "pointer",
                          fontFamily: "inherit",
                          opacity: disabled ? 0.6 : 1,
                        }}
                      >
                        {pending === "start" ? "Starting…" : "Start"}
                      </button>
                      <button
                        type="button"
                        onClick={() => void startSelectedAgent(true)}
                        disabled={disabled}
                        title="Resume prior session (--continue)"
                        style={{
                          fontSize: 11,
                          padding: "4px 12px",
                          borderRadius: 4,
                          background: "var(--accent)",
                          color: "white",
                          border: "none",
                          cursor: disabled ? "not-allowed" : "pointer",
                          fontFamily: "inherit",
                          opacity: disabled ? 0.6 : 1,
                        }}
                      >
                        {pending === "start" ? "…" : "Continue"}
                      </button>
                    </div>
                  );
                })()}
                <button
                  type="button"
                  onClick={() => setShowAgentSettings(true)}
                  title="Agent settings (MCP config)"
                  style={{
                    background: "none",
                    border: "none",
                    color: "var(--text-muted)",
                    cursor: "pointer",
                    fontSize: 16,
                    padding: 4,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  ⚙
                </button>
              </div>
              )}
              {agent.runtime_kind !== "agent-v2" && agentOpError && (
                <div
                  style={{
                    padding: "6px 24px",
                    background: "rgba(250,82,82,0.1)",
                    borderBottom: "1px solid rgba(250,82,82,0.3)",
                    color: "var(--error)",
                    fontSize: 12,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span>Agent op failed: {agentOpError}</span>
                  <button
                    type="button"
                    onClick={() => setAgentOpError(null)}
                    style={{
                      background: "none",
                      border: "none",
                      color: "var(--text-muted)",
                      cursor: "pointer",
                      fontSize: 14,
                    }}
                    aria-label="Dismiss"
                  >
                    ×
                  </button>
                </div>
              )}
              {agent.runtime_kind === "agent-v2" ? (
                <AgentV2ChatViewWired
                  agent={agent}
                  authorHandle={effectiveHandle}
                  authorDisplayName={effectiveDisplayName}
                  workerStatus={v2WorkerStatus.get(agent.id)?.status
                    ?? (agentPresence.has(agent.id) ? "running" : "stopped")}
                  workerLastError={v2WorkerStatus.get(agent.id)?.lastError ?? null}
                  onAgentDeleted={() => {
                    setAgentReloadToken((n) => n + 1);
                    navigate(`/w/${agent.workspace_id}`);
                  }}
                />
              ) : (
                <AgentTerminal
                  agentId={agent.id}
                  agentName={agent.name}
                  presenceLive={agentPresence.has(agent.id)}
                />
              )}
            </div>
          )}
          {!channelId && !agentId && !settingsTab && !knowledgeOpen && (
            <div style={{ padding: 24, color: "var(--text-muted)" }}>
              Pick a channel or agent.
            </div>
          )}
          {(channelId && !channel) || (agentId && !agent) ? (
            <div style={{ padding: 24, color: "var(--text-muted)" }}>Loading…</div>
          ) : null}
        </main>
        {totalPendingDecisions > 0 && (
          <DecisionsSidebar
            workspaceId={workspace.id}
            refetchKey={decisionsRefetchKey}
          />
        )}
      </div>
      {showCreateChannel && (
        <CreateChannelDialog
          workspaceId={workspace.id}
          onClose={() => setShowCreateChannel(false)}
          onCreated={(c) => {
            setChannelReloadToken((n) => n + 1);
            goToChannel(c);
          }}
        />
      )}
      {/* KTK-317 — sidebar context menu + edit modal. Menu mounts at
       *  cursor coordinates from the right-click; clicking "Edit channel"
       *  closes the menu and opens the modal with that channel pre-loaded.
       *  EditChannelDialog calls PATCH name + PATCH description + PUT
       *  subscribers selectively when each field actually changed; on
       *  save we bump the channel-reload token so both sidebar lists
       *  re-fetch and the renamed row shows the new label without a
       *  full page refresh. */}
      {channelContextMenu && (
        <ChannelContextMenu
          channel={channelContextMenu.channel}
          x={channelContextMenu.x}
          y={channelContextMenu.y}
          onClose={() => setChannelContextMenu(null)}
          onEdit={() => setEditChannel(channelContextMenu.channel)}
        />
      )}
      {editChannel && (
        <EditChannelDialog
          workspaceId={workspace.id}
          channel={editChannel}
          onClose={() => setEditChannel(null)}
          onSaved={() => setChannelReloadToken((n) => n + 1)}
        />
      )}
      {showAgentSettings && agentId && agent && (
        <AgentSettingsDialog
          workspaceId={workspace.id}
          agentId={agent.id}
          agentName={agent.name}
          onClose={() => setShowAgentSettings(false)}
        />
      )}
      {showCreateAgent && (
        <AddAgentDialog
          workspaceId={workspace.id}
          onClose={() => setShowCreateAgent(false)}
          onCreated={(created) => {
            setAgentReloadToken((n) => n + 1);
            goToAgent(created);
          }}
        />
      )}
    </div>
  );
}

/**
 * KTK-259 — leaf row inside the workspace-config CollapsibleSection. Visually
 * mirrors ChannelList/AgentList rows (full-width left-aligned button,
 * var(--text-secondary), bg-hover on selection) so it feels like the rest of
 * the sidebar without restyling. `selected` highlights the row when the user
 * is currently on the page this item routes to.
 */
/**
 * KTK-303 — workspace-section row. Mirrors the channel/agent row pattern
 * (4px 8px 4px 22px padding, 14px icon slot, accent border-left + accent-soft
 * tint when selected) so the workspace section reads as a tidy paired list
 * (Settings ⚙ / Knowledge 📖) alongside the rest of the sidebar.
 */
function WorkspaceConfigItem({
  icon,
  onClick,
  selected = false,
  children,
}: {
  icon: React.ReactNode;
  onClick: () => void;
  selected?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: "100%",
        textAlign: "left",
        padding: "4px 8px 4px 22px",
        background: selected
          ? "color-mix(in oklab, var(--accent-soft) 60%, var(--paper-2))"
          : "transparent",
        border: "none",
        borderLeft: `2px solid ${selected ? "var(--accent)" : "transparent"}`,
        borderRadius: 0,
        cursor: "pointer",
        fontFamily: "inherit",
        fontSize: 13,
        color: selected ? "var(--ink)" : "var(--ink-2)",
        display: "flex",
        alignItems: "center",
        gap: 8,
        minHeight: 24,
        transition: "background 80ms ease",
      }}
      onMouseEnter={(e) => {
        if (!selected) e.currentTarget.style.background = "var(--paper-3)";
      }}
      onMouseLeave={(e) => {
        if (!selected) e.currentTarget.style.background = "transparent";
      }}
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 14,
          color: selected ? "var(--accent-ink)" : "var(--ink-4)",
          flexShrink: 0,
        }}
      >
        {icon}
      </span>
      <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
        {children}
      </span>
    </button>
  );
}

/**
 * Gear glyph for the Settings row. Sized + stroked to match the platform
 * glyphs in AgentList/ScoutList so the workspace section reads as a peer
 * to the other sidebar groups.
 */
function GearIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="8" cy="8" r="2.2" />
      <path d="M8 1.5v2M8 12.5v2M1.5 8h2M12.5 8h2M3.4 3.4l1.4 1.4M11.2 11.2l1.4 1.4M3.4 12.6l1.4-1.4M11.2 4.8l1.4-1.4" />
    </svg>
  );
}

/** Open-book glyph for the Knowledge row — paired with the gear above. */
function BookIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M2 3.2c1.6-.4 3.4-.4 5 .3.4.2.6.5.6.9v9.1c0-.4-.2-.7-.6-.9-1.6-.7-3.4-.7-5-.3z" />
      <path d="M14 3.2c-1.6-.4-3.4-.4-5 .3-.4.2-.6.5-.6.9v9.1c0-.4.2-.7.6-.9 1.6-.7 3.4-.7 5-.3z" />
    </svg>
  );
}

// AgentV2ChatView wrapper — composes the agent + workspace-owner
// identity into the roster shape AgentV2ChatView expects, hands it
// the agent's stored system prompt, and wires the relay RPCs the
// runtime exposes for KTK-213 (interrupt) and KTK-217 (per-model
// system-prompt push). Live channel-message subscription happens at
// the channel layer the agent is talking through — the consumer
// navigates to the agent's primary channel via the existing channel
// view today; the dedicated chat-surface mount here surfaces the
// chrome + DM-side affordances.
function AgentV2ChatViewWired({
  agent,
  authorHandle,
  authorDisplayName,
  workerStatus,
  workerLastError,
  onAgentDeleted,
}: {
  agent: Agent;
  authorHandle: string;
  authorDisplayName: string;
  workerStatus: "running" | "stopped" | "error";
  workerLastError: string | null;
  onAgentDeleted: () => void;
}) {
  const relay = useRelay();
  const ctx = useDataContext();
  // Live message list — subscribed to the agent's DM channel via the
  // existing relay. Hub's createAgentV2 endpoint provisions a 1-on-1
  // channel and stamps `agent.dm_channel_id`; older Agent V2 rows
  // (pre-rocky's endpoint) won't have the field, so we display the
  // chat chrome but with no message stream.
  const dmChannelId = agent.dm_channel_id ?? null;
  const [messages, setMessages] = useState<AgentV2Message[]>([]);
  const [busy, setBusy] = useState(false);
  const roster: AgentV2Roster = useMemo(
    () => ({
      ai: {
        id: "ai",
        name: agent.name,
        role: "ai",
        initials: initialsFor(agent.name),
        colorVar: "--ai",
      },
      [authorHandle]: {
        id: authorHandle,
        name: authorDisplayName,
        role: "user",
        initials: initialsFor(authorDisplayName),
        colorVar: "--accent",
        colorSoft: "--accent-soft",
        colorInk: "--accent-ink",
      },
    }),
    [agent.name, authorHandle, authorDisplayName],
  );

  // KTK-211: load + subscribe. Initial fetch via the existing
  // getChannelMessages adapter; live appends via relay.onMessage on
  // 'channel_message' frames. `metadata.thinking` from KTK-216 is
  // projected into a `think` block above the markdown body so the
  // mockup's collapsible thought affordance lands.
  useEffect(() => {
    if (!dmChannelId) return;
    let cancelled = false;
    void getChannelMessages(ctx, agent.workspace_id, dmChannelId, { limit: 100 }).then(
      (rows) => {
        if (cancelled) return;
        setMessages(rows.map((m) => messageToAgentV2(m, authorHandle)));
      },
    );
    return () => {
      cancelled = true;
    };
  }, [ctx, agent.workspace_id, dmChannelId, authorHandle]);

  useEffect(() => {
    if (!dmChannelId) return;
    // KTK-236 — track tool_dispatch.call_id → block index inside the
    // live bubble so tool_result can update the matching block in
    // place. Cleared when the bubble is dropped (channel_post arrival
    // or turn_end).
    const toolCallToBlockIdx = new Map<string, number>();
    return relay.onMessage((frame: {
      type?: string;
      channel_id?: string;
      message_id?: string;
      content?: string;
      author_name?: string;
      author_type?: string;
      metadata?: Record<string, string>;
      timestamp?: string;
      turn_id?: string;
      activity?: AgentActivityFrame;
    }) => {
      if (frame.channel_id !== dmChannelId) return;
      if (frame.type === "channel_message") {
        const msg = messageFromRelayFrame(frame, authorHandle);
        if (!msg) return;
        // KTK-236 — when the canonical agent post for this turn lands,
        // drop the live bubble (its content is now persisted in the
        // post itself, with reasoning + tool blocks projected via the
        // KTK-216 / KTK-230 metadata round-trip). If the depth loop
        // continues (multi-step turn), the next agent_activity event
        // re-creates the bubble.
        const dropLive = frame.author_type === "agent";
        setMessages((prev) => {
          const filtered = dropLive ? prev.filter((m) => !m.id.startsWith("live-")) : prev;
          if (filtered.some((m) => m.id === msg.id)) return filtered;
          return [...filtered, msg];
        });
        if (dropLive) toolCallToBlockIdx.clear();
        // KTK-211: when the runtime emits a turn that ends with a
        // channel_post the agent goes idle. The agent's own outbound
        // post turning busy=false here is the right signal.
        if (frame.author_type === "agent") setBusy(false);
        return;
      }
      // KTK-236 — live activity feed. Runtime emits ephemeral
      // `agent_activity` frames keyed by turn_id at every observable
      // boundary (turn_start / text_delta / reasoning_delta /
      // tool_dispatch / tool_result / turn_end). We render them into
      // an in-progress bubble (id = `live-<turnId>`) using the same
      // block kinds (md / think / tool / stream) that get persisted,
      // so live and replay look identical.
      if (frame.type === "agent_activity" && frame.turn_id && frame.activity) {
        const turnId = frame.turn_id;
        const ev = frame.activity;
        const liveId = `live-${turnId}`;
        if (ev.kind === "turn_end") {
          setMessages((prev) => prev.filter((m) => m.id !== liveId));
          toolCallToBlockIdx.clear();
          return;
        }
        setMessages((prev) => {
          let idx = prev.findIndex((m) => m.id === liveId);
          let next: AgentV2Message[];
          if (idx < 0) {
            const startedAt =
              ev.kind === "turn_start" && typeof ev.started_at === "string"
                ? ev.started_at
                : new Date().toISOString();
            const fresh: AgentV2Message = {
              id: liveId,
              author: "ai",
              time: formatTime(startedAt),
              blocks: [],
              model: activeModelRef.current || undefined,
            };
            next = [...prev, fresh];
            idx = next.length - 1;
          } else {
            next = prev;
          }
          if (ev.kind === "turn_start") return next;
          const live = next[idx];
          if (!live) return next;
          const blocks: AgentV2Block[] = [...live.blocks];
          if (ev.kind === "text_delta" && typeof ev.text === "string") {
            const last = blocks[blocks.length - 1];
            if (last && last.kind === "stream") {
              blocks[blocks.length - 1] = { kind: "stream", content: last.content + ev.text };
            } else {
              blocks.push({ kind: "stream", content: ev.text });
            }
          } else if (ev.kind === "reasoning_delta" && typeof ev.text === "string") {
            const last = blocks[blocks.length - 1];
            if (last && last.kind === "think") {
              blocks[blocks.length - 1] = {
                kind: "think",
                content: last.content + ev.text,
                duration: last.duration,
                status: "running",
              };
            } else {
              blocks.push({ kind: "think", content: ev.text, status: "running" });
            }
          } else if (ev.kind === "tool_dispatch" && typeof ev.call_id === "string" && typeof ev.name === "string") {
            toolCallToBlockIdx.set(ev.call_id, blocks.length);
            blocks.push({
              kind: "tool",
              tool: ev.name,
              toolKind: toolKindForName(ev.name),
              args: ev.args,
              status: "running",
            });
          } else if (ev.kind === "tool_result" && typeof ev.call_id === "string") {
            const blockIdx = toolCallToBlockIdx.get(ev.call_id);
            if (blockIdx !== undefined && blockIdx < blocks.length) {
              const existing = blocks[blockIdx];
              if (existing && existing.kind === "tool") {
                const durSec =
                  typeof ev.duration_ms === "number" ? (ev.duration_ms / 1000).toFixed(1) : undefined;
                blocks[blockIdx] = {
                  ...existing,
                  status: ev.errored ? "error" : "done",
                  output: typeof ev.output === "string" ? ev.output : existing.output,
                  duration: durSec ? `${durSec}s` : existing.duration,
                };
              }
            }
          }
          const updated: AgentV2Message = { ...live, blocks };
          return [...next.slice(0, idx), updated, ...next.slice(idx + 1)];
        });
      }
    });
  }, [relay, dmChannelId, authorHandle]);

  // KTK-217: editor's source-of-truth. Seeded from the agent row;
  // bumped after a successful save so the editor's dirty-state
  // detector resets to the just-saved value (no page refresh
  // required). Hub now stores the full {default, perModel} variants
  // map serialized into agent.system_prompt — accept both legacy
  // flat-string and JSON-object shapes here so the editor handles
  // pre-KTK-217 rows cleanly.
  const seededPrompt = useMemo<SystemPromptVariants>(() => {
    return parseSystemPrompt(agent.system_prompt);
  }, [agent.system_prompt]);
  const [savedPrompt, setSavedPrompt] = useState<SystemPromptVariants>(seededPrompt);
  useEffect(() => {
    setSavedPrompt(seededPrompt);
  }, [seededPrompt]);

  // KTK-213: Stop button → emit `agent_interrupt` Hub frame; runtime's
  // channel-bus routes to the agent's worker IPC, which sets the same
  // interruptRequested flag KTK-207's mid-turn-injection path uses.
  const onStop = useCallback((): void => {
    relay.send({
      type: "agent_interrupt",
      agent_id: agent.id,
    } as Parameters<typeof relay.send>[0]);
  }, [relay, agent.id]);

  // KTK-217: SystemPromptEditor save → PATCH /api/agents/:id/system-prompt.
  // Hub persists the variants map on the agent row and fans an
  // `agent_set_system_prompt` frame to the owning runtime so the
  // worker re-reads on the next turn without a restart. After the
  // round-trip we update local saved-state so the editor's dirty
  // detector resets without a page refresh.
  const onSaveSystemPrompt = useCallback(
    async (next: SystemPromptVariants): Promise<void> => {
      await ctx.hub.setAgentSystemPrompt(agent.id, next);
      setSavedPrompt(next);
    },
    [ctx.hub, agent.id],
  );

  // KTK-221: Danger Zone delete. Hub removes the agent row + DM
  // channel + history server-side and broadcasts agent_delete to
  // the runtime which stops the worker. After the call resolves we
  // call onAgentDeleted so the parent Shell can navigate away +
  // refresh the agents sidebar (it owns those handles).
  const onDelete = useCallback(async (): Promise<void> => {
    await ctx.hub.deleteAgent(agent.id);
    onAgentDeleted();
  }, [ctx.hub, agent.id, onAgentDeleted]);

  // KTK-222: ModelPicker save → PATCH /api/agents/:id/model. Hub
  // persists the new model on the agent row and fans an
  // `agent_set_model` frame to the owning runtime so the worker
  // picks up the new model on the next turn. We track active model
  // locally so the picker + model badge reflect the saved value
  // without round-tripping a list-agents refresh.
  const [activeModel, setActiveModel] = useState<string>(agent.model);
  useEffect(() => {
    setActiveModel(agent.model);
  }, [agent.model]);
  // KTK-249 — live bubble's `model` is best-effort: stamped from the
  // web client's view of the active model when turn_start arrives.
  // The canonical post that follows carries the runtime's
  // metadata.model, which overrides the bubble when it lands. A ref
  // (rather than including activeModel in the activity-listener
  // useEffect deps) avoids re-binding the relay listener every time
  // the user swaps models.
  const activeModelRef = useRef<string>(activeModel);
  useEffect(() => {
    activeModelRef.current = activeModel;
  }, [activeModel]);
  const onSetModel = useCallback(
    async (next: string): Promise<void> => {
      await ctx.hub.setAgentModel(agent.id, next);
      setActiveModel(next);
    },
    [ctx.hub, agent.id],
  );

  // KTK-224: channel subscriptions panel — load workspace channels +
  // current subs in parallel; toggling fires
  // hub.subscribeAgent/unsubscribeAgent which Hub fans out as an
  // agent_set_channels frame to the runtime.
  const [channelOptions, setChannelOptions] = useState<Channel[] | null>(null);
  const [subscribedChannelIds, setSubscribedChannelIds] = useState<Set<string> | null>(null);
  useEffect(() => {
    let cancelled = false;
    void Promise.all([
      ctx.hub.listChannels(agent.workspace_id),
      ctx.hub.listAgentSubscriptions(agent.workspace_id, agent.id),
    ]).then(([cs, subs]) => {
      if (cancelled) return;
      setChannelOptions(cs);
      setSubscribedChannelIds(new Set(subs.map((c) => c.id)));
    });
    return () => {
      cancelled = true;
    };
  }, [ctx.hub, agent.workspace_id, agent.id]);
  const onToggleSubscription = useCallback(
    async (channelId: string, nextSubscribed: boolean): Promise<void> => {
      if (nextSubscribed) {
        await ctx.hub.subscribeAgent(agent.workspace_id, agent.id, channelId);
        setSubscribedChannelIds((prev) =>
          prev ? new Set([...prev, channelId]) : new Set([channelId]),
        );
      } else {
        await ctx.hub.unsubscribeAgent(agent.workspace_id, agent.id, channelId);
        setSubscribedChannelIds((prev) =>
          prev ? new Set([...prev].filter((id) => id !== channelId)) : new Set(),
        );
      }
    },
    [ctx.hub, agent.workspace_id, agent.id],
  );

  // KTK-223: per-agent MCP server config — load via getMcpJsonConfig +
  // save via setMcpJsonConfig. Hub fans an `agent_set_mcp_servers`
  // frame to the runtime so the worker reconnects between turns.
  const [mcpConfig, setMcpConfig] = useState<Record<string, unknown> | null>(null);
  useEffect(() => {
    let cancelled = false;
    void ctx.hub.getMcpJsonConfig("agent", agent.id).then((cfg) => {
      if (!cancelled) setMcpConfig(cfg);
    });
    return () => {
      cancelled = true;
    };
  }, [ctx.hub, agent.id]);
  const onSaveMcpConfig = useCallback(
    async (next: Record<string, unknown>): Promise<void> => {
      await ctx.hub.setMcpJsonConfig("agent", agent.id, next);
      setMcpConfig(next);
    },
    [ctx.hub, agent.id],
  );

  // KTK-225: worker lifecycle. Status pip is driven by the same
  // agentPresence map that powers Claude Code's sidebar pip — Hub
  // merges runtime-broadcast `agent_status` frames into
  // `listAgentPresence`. Start/Stop hit Hub endpoints which fan
  // `agent_start` (no payload — runtime restarts from on-disk
  // config) / `agent_stop` to the host.
  const onStartWorker = useCallback(async (): Promise<void> => {
    await ctx.hub.startAgentRuntime(agent.id);
  }, [ctx.hub, agent.id]);
  const onStopWorker = useCallback(async (): Promise<void> => {
    await ctx.hub.stopAgentRuntime(agent.id);
  }, [ctx.hub, agent.id]);

  // KTK-211: composer onSend → sendMessage on the agent's DM channel.
  // Identical path the existing ChannelView uses, just bound to the
  // agent's DM channel and stamped with the workspace owner's
  // handle/displayName. The runtime sees this as a normal channel
  // inbound and kicks an LLM turn (KTK-208's loop).
  const onSend = useCallback(
    (text: string): void => {
      if (!dmChannelId) return;
      // Insert a pending row immediately so the user sees their message
      // before the round-trip to Hub completes. Reconcile with the real
      // id when sendMessage resolves; roll back on failure. The relay's
      // channel_message frame doesn't echo the sender's own post back,
      // so the optimistic row is the only path for the user's message
      // to land on their own chat surface.
      const pendingId = `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const optimistic: AgentV2Message = {
        id: pendingId,
        author: authorHandle,
        time: formatTime(new Date().toISOString()),
        blocks: [{ kind: "md", content: text }],
      };
      setMessages((prev) => [...prev, optimistic]);
      // Optimistic busy=true so the StopButton appears immediately;
      // gets reset when the agent's response lands above.
      setBusy(true);
      void (async () => {
        try {
          const sent = await sendMessage(ctx, agent.workspace_id, dmChannelId, {
            content: text,
            authorName: authorDisplayName || authorHandle,
            authorType: "human",
          });
          setMessages((prev) => {
            const withoutPending = prev.filter((m) => m.id !== pendingId);
            if (withoutPending.some((m) => m.id === sent.id)) return withoutPending;
            return [...withoutPending, messageToAgentV2(sent, authorHandle)];
          });
        } catch (err) {
          setMessages((prev) => prev.filter((m) => m.id !== pendingId));
          setBusy(false);
          console.error("agent v2 send failed", err);
        }
      })();
    },
    [ctx, agent.workspace_id, dmChannelId, authorHandle, authorDisplayName],
  );

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <AgentV2ChatView
        title={agent.name}
        agentName={agent.name}
        roster={roster}
        participants={[authorHandle]}
        postingAs={authorHandle}
        onSelectAs={() => undefined}
        messages={messages}
        onSend={onSend}
        busy={busy}
        onStop={onStop}
        onSaveSystemPrompt={onSaveSystemPrompt}
        onSetModel={onSetModel}
        channelOptions={channelOptions ?? undefined}
        subscribedChannelIds={subscribedChannelIds ?? undefined}
        dmChannelId={dmChannelId}
        onToggleSubscription={onToggleSubscription}
        mcpConfig={mcpConfig ?? undefined}
        onSaveMcpConfig={onSaveMcpConfig}
        workerStatus={workerStatus}
        workerLastError={workerLastError}
        onStartWorker={onStartWorker}
        onStopWorker={onStopWorker}
        onDelete={onDelete}
        activeModel={activeModel}
        systemPrompt={savedPrompt}
        theme="dark"
        density="compact"
        layout="bubble"
      />
    </div>
  );
}

// KTK-229/230 — Hub serializes message metadata as a JSON string when
// returning channel-history rows from D1, but the typed Message field
// is `Record<string, unknown>`. Without parsing, all the typeof checks
// below see a string and silently drop the token pill / thinking /
// tool blocks on every reload. Parse on the way in so downstream
// helpers see the real shape regardless of which path the metadata
// arrived on (history-load string vs WSS-broadcast object).
function coerceMetadata(meta: unknown): Record<string, unknown> | null {
  if (!meta) return null;
  if (typeof meta === "object") return meta as Record<string, unknown>;
  if (typeof meta === "string") {
    try {
      const parsed = JSON.parse(meta);
      return parsed && typeof parsed === "object" ? parsed : null;
    } catch {
      return null;
    }
  }
  return null;
}

// KTK-211: project a stored channel Message into the AgentV2 chat
// surface's block-typed shape. md is the default; metadata.thinking
// (KTK-216) becomes a leading `think` block; multipart images are
// skipped here for v1 (chat surface renders inline image refs in the
// markdown body via react-markdown).
function messageToAgentV2(
  m: import("@kritaka/adapter").Message,
  humanHandle: string,
): AgentV2Message {
  const blocks: AgentV2Block[] = [];
  const meta = coerceMetadata(m.metadata);
  const thinking = meta && typeof meta.thinking === "string" ? meta.thinking : null;
  const thinkingDur = meta && typeof meta.thinking_duration_ms === "string"
    ? `${(parseInt(meta.thinking_duration_ms, 10) / 1000).toFixed(1)}s`
    : undefined;
  if (thinking) {
    blocks.push({ kind: "think", content: thinking, duration: thinkingDur });
  }
  // KTK-230 — tool blocks come BEFORE the markdown body so the chat
  // surface stacks "I ran bash, then web_search, then said this".
  for (const tool of toolBlocksFromMetadata(meta)) blocks.push(tool);
  blocks.push({ kind: "md", content: m.content });
  return {
    id: m.id,
    // Roster keys are "ai" + the wired-up `humanHandle`. Agent v2 DMs
    // only have one human (the workspace owner), so project all
    // non-agent posts to that handle so the roster lookup hits.
    // (The chat surface drops messages whose author isn't in the
    // roster — KTK-225 follow-up where DM messages were silently
    // dropped because m.author_name was the display name, not the
    // handle.)
    author: m.author_type === "agent" ? "ai" : humanHandle,
    time: formatTime(m.created_at),
    blocks,
    tokens: tokensFromMetadata(meta),
    model: modelFromMetadata(meta),
  };
}

// Same projection but from the relay's flat frame shape (matches what
// channel-bus emits; the existing ChannelView uses the same shape via
// relayFrameToMessage). Metadata is coerced through the same parser
// as the history path so a string-shaped frame (Hub returns history
// rows with metadata as a JSON string and may broadcast the same way)
// still surfaces token / thinking / tool blocks.
function messageFromRelayFrame(
  frame: {
    message_id?: string;
    content?: string;
    author_name?: string;
    author_type?: string;
    metadata?: unknown;
    timestamp?: string;
  },
  humanHandle: string,
): AgentV2Message | null {
  if (!frame.message_id || typeof frame.content !== "string") return null;
  const meta = coerceMetadata(frame.metadata);
  const blocks: AgentV2Block[] = [];
  const thinking = meta && typeof meta.thinking === "string" ? meta.thinking : null;
  const thinkingDur =
    meta && typeof meta.thinking_duration_ms === "string"
      ? `${(parseInt(meta.thinking_duration_ms, 10) / 1000).toFixed(1)}s`
      : undefined;
  if (thinking) blocks.push({ kind: "think", content: thinking, duration: thinkingDur });
  // KTK-230 — same prepend-tool-blocks pattern as the historical path.
  for (const tool of toolBlocksFromMetadata(meta)) blocks.push(tool);
  blocks.push({ kind: "md", content: frame.content });
  return {
    id: frame.message_id,
    author: frame.author_type === "agent" ? "ai" : humanHandle,
    time: formatTime(frame.timestamp ?? new Date().toISOString()),
    blocks,
    tokens: tokensFromMetadata(meta),
    model: modelFromMetadata(meta),
  };
}

// KTK-249 — pull `metadata.model` (the OpenRouter model id stamped by
// the runtime on every channel_post in agent-worker.ts). Returns
// undefined for human posts and pre-KTK-249 history; the chat
// surface's tokmeta__model badge gates on truthiness.
function modelFromMetadata(
  meta: Record<string, unknown> | null | undefined,
): string | undefined {
  if (!meta) return undefined;
  return typeof meta.model === "string" && meta.model.length > 0 ? meta.model : undefined;
}

// KTK-230 — deserialize the runtime's per-turn tool calls (bash,
// web_search, fs ops, MCP tools) from message metadata into chat-
// surface `kind: "tool"` blocks. Runtime stamps `metadata.tool_calls`
// as a JSON array of `{ name, args, output, durationMs }` on the first
// channel_post of the turn (one-shot pattern matching pendingReasoning
// + pendingTurnTokens). Internal channel/decision tools are filtered
// runtime-side so they never reach this projection.
//
// Returns [] when the message carries no tool calls (human posts,
// pre-KTK-230 messages, agent turns that didn't dispatch any tool).
function toolBlocksFromMetadata(
  meta: Record<string, unknown> | null | undefined,
): AgentV2Block[] {
  if (!meta) return [];
  const raw = meta.tool_calls;
  if (typeof raw !== "string" || raw.length === 0) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  const out: AgentV2Block[] = [];
  for (const entry of parsed) {
    if (!entry || typeof entry !== "object") continue;
    const e = entry as { name?: unknown; args?: unknown; output?: unknown; durationMs?: unknown };
    const name = typeof e.name === "string" ? e.name : null;
    if (!name) continue;
    const output = typeof e.output === "string" ? e.output : "";
    const durationMs = typeof e.durationMs === "number" ? e.durationMs : null;
    out.push({
      kind: "tool",
      tool: name,
      toolKind: toolKindForName(name),
      args: e.args,
      output,
      duration: durationMs != null ? `${(durationMs / 1000).toFixed(1)}s` : undefined,
      status: "done",
    });
  }
  return out;
}

// Map runtime tool name → chat-surface ToolKind so the right icon
// renders. Anything not in the table falls through to "tool" (generic
// gear icon). MCP tools come through with provider-prefixed names
// (`<server>:<tool>`), which we treat as generic.
// KTK-236 — wire shape for the runtime's `agent_activity` HubFrame
// payload. Mirrors AgentActivityEvent in the runtime's protocol.ts.
// Web client doesn't depend on the runtime package, so the type is
// duplicated here; the discriminator is `kind`.
type AgentActivityFrame =
  | { kind: "turn_start"; started_at?: string }
  | { kind: "text_delta"; text?: string }
  | { kind: "reasoning_delta"; text?: string }
  | { kind: "tool_dispatch"; call_id?: string; name?: string; args?: unknown; started_at?: string }
  | { kind: "tool_result"; call_id?: string; output?: string; duration_ms?: number; errored?: string }
  | { kind: "turn_end"; finished_at?: string };

function toolKindForName(name: string): import("@kritaka/ui").ToolKind {
  switch (name) {
    case "bash":
      return "bash";
    case "web_search":
      return "web";
    case "read":
    case "glob":
    case "grep":
      return "file";
    case "write":
    case "edit":
      return "edit";
    default:
      return "tool";
  }
}

// KTK-229 — pull the runtime's per-turn token / cost meta out of
// message metadata. Runtime stamps the first channel_post of each
// turn with `prompt_tokens` / `completion_tokens` / `cached_input_tokens`
// / `cost_usd`; the chat surface renders the resulting `tokens` field
// as an inline pill on the message. Returns undefined when the message
// carries no token meta (human posts, older messages, model providers
// that don't report usage).
function tokensFromMetadata(
  meta: Record<string, unknown> | null | undefined,
): { in: number; out: number; cost: string } | undefined {
  if (!meta) return undefined;
  const promptStr = typeof meta.prompt_tokens === "string" ? meta.prompt_tokens : null;
  const completionStr = typeof meta.completion_tokens === "string" ? meta.completion_tokens : null;
  if (!promptStr && !completionStr) return undefined;
  const promptIn = promptStr ? parseInt(promptStr, 10) : 0;
  const completionOut = completionStr ? parseInt(completionStr, 10) : 0;
  if (!Number.isFinite(promptIn) && !Number.isFinite(completionOut)) return undefined;
  const costStr = typeof meta.cost_usd === "string" ? meta.cost_usd : null;
  const cost = costStr ? `$${parseFloat(costStr).toFixed(4)}` : "—";
  return {
    in: Number.isFinite(promptIn) ? promptIn : 0,
    out: Number.isFinite(completionOut) ? completionOut : 0,
    cost,
  };
}

function formatTime(iso: string): string {
  // Hub's REST /messages returns created_at as a Postgres TIMESTAMP
  // serialization ("2026-04-27 07:40:31") — no TZ marker. The relay's
  // WSS frame.timestamp is proper ISO with Z. Without normalization the
  // browser parses the SQL form as local time and the same conversation
  // ends up with two different render zones (historical UTC literal vs
  // live local-shifted). Treat un-zoned input as UTC.
  let s = iso;
  if (s.includes(" ") && !s.includes("T")) s = s.replace(" ", "T");
  if (!/[Zz]|[+-]\d{2}:?\d{2}$/.test(s)) s = `${s}Z`;
  const d = new Date(s);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

// KTK-217: Hub stores the {default, perModel} variants map serialized
// into agent.system_prompt. Older rows (pre-KTK-217) are flat strings.
// Decode either shape into the editor's variants type.
function parseSystemPrompt(raw: string | null | undefined): SystemPromptVariants {
  if (!raw) return { default: "", perModel: {} };
  if (raw.trim().startsWith("{")) {
    try {
      const parsed = JSON.parse(raw) as { default?: unknown; perModel?: unknown };
      const def = typeof parsed.default === "string" ? parsed.default : "";
      const perModel: Record<string, string> = {};
      if (parsed.perModel && typeof parsed.perModel === "object") {
        for (const [k, v] of Object.entries(parsed.perModel)) {
          if (typeof v === "string") perModel[k] = v;
        }
      }
      return { default: def, perModel };
    } catch {
      // fall through — not actually JSON, treat as flat
    }
  }
  return { default: raw, perModel: {} };
}

function initialsFor(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0] ?? "";
  if (parts.length === 1) return first.slice(0, 2).toUpperCase() || "?";
  const last = parts[parts.length - 1] ?? "";
  return ((first[0] ?? "") + (last[0] ?? "")).toUpperCase() || "?";
}

function CenteredMessage({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="app-pre-shell"
      style={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: "var(--bg-primary)",
        color: "var(--text-muted)",
        fontSize: 13,
        padding: 24,
        textAlign: "center",
      }}
    >
      {children}
    </div>
  );
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function usePersistedWidth(
  key: string,
  fallback: number,
): [number, (updater: (prev: number) => number) => void] {
  const [width, setWidth] = useState<number>(() => {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return fallback;
      const parsed = Number.parseInt(raw, 10);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
    } catch {
      return fallback;
    }
  });
  const update = useCallback(
    (updater: (prev: number) => number) => {
      setWidth((prev) => {
        const next = updater(prev);
        try {
          localStorage.setItem(key, String(next));
        } catch {
          // Quota / private browsing — width still applies in-session.
        }
        return next;
      });
    },
    [key],
  );
  return [width, update];
}

function ColumnResizer({ onResize }: { onResize: (delta: number) => void }) {
  const [dragging, setDragging] = useState(false);
  useEffect(() => {
    if (!dragging) return;
    const handleMove = (e: MouseEvent) => {
      // movementX is the pixel delta since the last mousemove, which is what
      // we feed directly into the column width — no reference capture needed.
      onResize(e.movementX);
    };
    const handleUp = () => setDragging(false);
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    // Prevent text selection while dragging.
    const prevSelect = document.body.style.userSelect;
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
      document.body.style.userSelect = prevSelect;
      document.body.style.cursor = "";
    };
  }, [dragging, onResize]);
  return (
    <div
      onMouseDown={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      style={{
        cursor: "col-resize",
        background: dragging ? "var(--accent)" : "var(--border)",
        transition: dragging ? "none" : "background 0.15s",
      }}
      onMouseEnter={(e) => {
        if (!dragging) e.currentTarget.style.background = "var(--accent)";
      }}
      onMouseLeave={(e) => {
        if (!dragging) e.currentTarget.style.background = "var(--border)";
      }}
    />
  );
}
